import type {
  ClientMessage,
  Language,
  LobbySettings,
  PlayerProfile,
  ServerMessage
} from "@marshmallow-duel/simulation";

type SignalMessage = Extract<ServerMessage, { type: "signal" }>;

const webSocketUrl = (): string => {
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  if (configured) return configured;
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws`;
};

export class NetworkClient {
  private readonly socket: WebSocket;
  private readonly listeners = new Set<(message: ServerMessage) => void>();
  private readonly signalListeners = new Set<(message: SignalMessage) => void>();
  private readonly pendingSignals: SignalMessage[] = [];
  private readonly queuedMessages: ClientMessage[] = [];
  private pingTimer = 0;
  private intentionallyClosed = false;
  latency = 0;
  onDisconnected: () => void = () => undefined;

  constructor(language: Language, profile: PlayerProfile) {
    this.socket = new WebSocket(webSocketUrl());
    this.socket.addEventListener("open", () => {
      this.sendNow({ type: "hello", language, profile });
      for (const message of this.queuedMessages.splice(0)) this.sendNow(message);
      this.pingTimer = window.setInterval(() => this.sendNow({ type: "ping", sentAt: Date.now() }), 3_000);
    });
    this.socket.addEventListener("message", this.onSocketMessage);
    this.socket.addEventListener("close", () => {
      clearInterval(this.pingTimer);
      if (!this.intentionallyClosed) this.onDisconnected();
    });
  }

  subscribe(listener: (message: ServerMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeSignals(listener: (message: SignalMessage) => void): () => void {
    this.signalListeners.add(listener);
    for (const message of this.pendingSignals.splice(0)) listener(message);
    return () => this.signalListeners.delete(listener);
  }

  createRoom(settings: LobbySettings): void { this.send({ type: "createRoom", settings }); }
  joinRoom(code: string): void { this.send({ type: "joinRoom", code }); }
  leaveRoom(): void { this.send({ type: "leaveRoom" }); }
  setReady(ready: boolean): void { this.send({ type: "setReady", ready }); }
  updateProfile(profile: PlayerProfile): void { this.send({ type: "updateProfile", profile }); }
  updateSettings(settings: LobbySettings): void { this.send({ type: "updateSettings", settings }); }
  startRoom(): void { this.send({ type: "startRoom" }); }
  sendSignal(targetPeerId: string, signal: { readonly description?: RTCSessionDescriptionInit; readonly candidate?: RTCIceCandidateInit }): void {
    this.send({ type: "signal", targetPeerId, ...signal });
  }

  close(): void {
    if (this.intentionallyClosed) return;
    this.intentionallyClosed = true;
    clearInterval(this.pingTimer);
    if (this.socket.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ type: "leaveRoom" } satisfies ClientMessage));
    this.socket.close(1000, "Client closed");
  }

  private send(message: ClientMessage): void {
    if (this.socket.readyState === WebSocket.OPEN) this.sendNow(message);
    else if (this.socket.readyState === WebSocket.CONNECTING) this.queuedMessages.push(message);
  }

  private sendNow(message: ClientMessage): void {
    if (this.socket.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private readonly onSocketMessage = (event: MessageEvent<string>): void => {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      if (message.type === "pong") this.latency = Math.max(0, Math.round((Date.now() - message.sentAt) / 2));
      if (message.type === "signal") {
        if (this.signalListeners.size === 0) this.pendingSignals.push(message);
        else for (const listener of this.signalListeners) listener(message);
      }
      for (const listener of this.listeners) listener(message);
    } catch {
      // A malformed signaling packet is ignored; a valid later room update can recover the UI.
    }
  };
}
